import { Paper, Button, Box, CircularProgress } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";

import type { SelectAudienceOption } from "@/features/BroadcastV2/components/SelectAudienceField";
import type { VariationDraft } from "@/features/BroadcastV2/store/variationTypes";
import type { CreateBroadcastRequestV2 } from "@/features/BroadcastV2/types";
import type { TextComposerAuthoringState } from "@/features/TextComposerV2/editor/authoringState";
import type { CreateFramePayloadParsed } from "@/frames/schemas";
import type { Frame } from "@/types/dragon-tiles";

import {
  broadcastV2Keys,
  createBroadcastV2ViaAudiencesApi,
  updateBroadcastStatusViaAudiencesApi,
} from "@/api/broadcastV2Audiences";
import {
  createFrame,
  frameQueryOptions,
  useDragonTiles,
} from "@/api/dragon-tiles";
import { getBroadcastInnerTileIdFromDragonTiles } from "@/api/dragon-tiles/utils";
import { useCanUseRcs } from "@/api/siteCapabilities";
import { BroadcastV2ComposeSmsForm } from "@/features/BroadcastV2/components/BroadcastV2ComposeSmsForm";
import { RightColumnVariationTabs } from "@/features/BroadcastV2/components/RightColumnVariationTabs";
import { parseFrameSeed } from "@/features/BroadcastV2/components/shared/parseFrameSeed";
import {
  broadcastV2ComposeDraftFormSchema,
  buildCreateBroadcastFramePayload,
  buildCreateSmsBroadcastRequestV2,
  buildDraftSmsBroadcastRequestV2,
} from "@/features/BroadcastV2/createBroadcastPayload";
import { calendarSourceStamp } from "@/features/BroadcastV2/hooks/useCalendarOwnedLock";
import {
  useAddSmsVariation,
  useVariationDraftStores,
} from "@/features/BroadcastV2/hooks/useVariationDraftStores";
import {
  BroadcastComposeProvider,
  useBroadcastComposeStore,
  useBroadcastComposeStoreApi,
  useBroadcastComposeStoreShallow,
  useIsAbDurationInvalid,
} from "@/features/BroadcastV2/store/broadcastComposeStore";
import { defaultScheduleWindowSendAt } from "@/features/BroadcastV2/store/broadcastComposeStoreFactory";
import {
  makeSmsVariation,
  smsVariationFrameName,
} from "@/features/BroadcastV2/store/variationTypes";
import { CreateMessageField } from "@/features/TextComposerV2/compose/CreateMessageField";
import {
  AuthoringColumn,
  ComposerLayout,
} from "@/features/TextComposerV2/compose/layout.styles";
import { useMessageDraftSelectors } from "@/features/TextComposerV2/compose/useMessageDraftSelectors";
import {
  selectFrameOutput,
  selectIsAuthoringValid,
  selectSelectedProductModel,
} from "@/features/TextComposerV2/editor/messageDraftSelectors";
import { PreviewPanel } from "@/features/TextComposerV2/preview/PreviewPanel";
import { useMessagePreview } from "@/features/TextComposerV2/preview/useMessagePreview";
import { useTextPreviewProfile } from "@/features/TextComposerV2/preview/useTextPreviewProfile";
import { OrionBackButton } from "@/orion/OrionComponents/OrionBackButton";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

interface BroadcastV2ComposeSmsPageProps {
  timezone?: string;
  sendWindowStart?: number | null;
  sendWindowEnd?: number | null;
  /** Tile whose text seeds the composer (Marketing Calendar plan flow). */
  pendingFrameId?: string;
  /** Set by the Marketing Calendar plan wizard; save returns there. */
  returnTo?: "marketing-calendar";
  /** Audiences to pre-select on the new draft (the plan wizard's campaign
   * audiences). */
  initialAudiences?: SelectAudienceOption[];
  /** Campaign date span ("YYYY-MM-DD") constraining scheduling in the plan
   * flow. */
  scheduleWindow?: { startDate: string; endDate: string } | null;
}

export function BroadcastV2ComposeSmsPage({
  timezone,
  sendWindowStart,
  sendWindowEnd,
  pendingFrameId,
  returnTo,
  initialAudiences,
  scheduleWindow,
}: BroadcastV2ComposeSmsPageProps = {}) {
  const { siteId = "" } = useParams({ strict: false });

  // When a tile seeds the composer, its parsed text must be ready before the
  // editor stores are created (seeds are read once per store).
  const seedFrameQuery = useQuery({
    ...frameQueryOptions(siteId, pendingFrameId ?? ""),
    enabled: Boolean(pendingFrameId),
  });
  const initialVariations = useMemo(
    () => [makeSmsVariation("", pendingFrameId ?? null)],
    [pendingFrameId],
  );

  if (pendingFrameId && !seedFrameQuery.data) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 10,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const seedsById = pendingFrameId
    ? { [initialVariations[0].id]: parseFrameSeed(seedFrameQuery.data) }
    : undefined;

  return (
    <BroadcastComposeProvider
      initialState={{
        primaryChannel: "SMS",
        variations: initialVariations,
        ...(initialAudiences?.length
          ? {
              audienceIds: initialAudiences.map((audience) => audience.id),
              audienceOptions: initialAudiences,
            }
          : {}),
        ...(scheduleWindow
          ? {
              scheduleWindow,
              // Prefill the campaign's first plannable send time so a plan
              // draft can never save with an empty schedule.
              scheduledAtIso: defaultScheduleWindowSendAt(
                scheduleWindow,
                timezone,
              ),
            }
          : {}),
      }}
    >
      <BroadcastV2ComposeSmsPageContent
        timezone={timezone}
        sendWindowStart={sendWindowStart}
        sendWindowEnd={sendWindowEnd}
        seedsById={seedsById}
        returnTo={returnTo}
      />
    </BroadcastComposeProvider>
  );
}

function SmsPreviewPanelWrapper({
  siteId,
  innerTileId,
}: {
  siteId: string;
  innerTileId: string | undefined;
}) {
  const {
    authoringState,
    frameOutput,
    previewRecipientViewMode,
    selectedProductModel,
    actions,
  } = useMessageDraftSelectors();
  const { selectedProfile, setSelectedProfile, previewFillParams } =
    useTextPreviewProfile(siteId, innerTileId);
  const { previewByChannel, previewMeta, refetchPreview } = useMessagePreview({
    siteId,
    previewFillParams,
  });

  return (
    <PreviewPanel
      siteId={siteId}
      authoringState={authoringState}
      frameOutput={frameOutput}
      previewByChannel={previewByChannel}
      previewRecipientViewMode={previewRecipientViewMode}
      onPreviewRecipientViewModeChange={actions.setPreviewRecipientViewMode}
      selectedProductModel={selectedProductModel}
      isPreviewError={previewMeta.isError}
      onPreviewRetry={refetchPreview}
      selectedProfile={selectedProfile}
      onSelectedProfileChange={setSelectedProfile}
      previewFillParams={previewFillParams}
    />
  );
}

function SmsMessageFieldWrapper({
  siteId,
  disabled,
  hideShell,
}: {
  siteId: string;
  disabled?: boolean;
  hideShell?: boolean;
}) {
  const { previewByChannel } = useMessagePreview({ siteId });
  const { messageDraftSelectors } = useMessageDraftSelectors({
    previewByChannel,
  });
  return (
    <CreateMessageField
      {...messageDraftSelectors}
      hideTitle
      hideShell={hideShell}
      disabled={disabled}
    />
  );
}

function BroadcastV2ComposeSmsPageContent({
  timezone,
  sendWindowStart,
  sendWindowEnd,
  returnTo,
  seedsById,
}: BroadcastV2ComposeSmsPageProps & {
  seedsById?: Record<string, TextComposerAuthoringState>;
}) {
  const navigate = useNavigate();
  const { siteId = "" } = useParams({ strict: false });
  const queryClient = useQueryClient();
  const storeApi = useBroadcastComposeStoreApi();
  // Sites without RCS open every variation's editor on the SMS/MMS side.
  const canUseRcs = useCanUseRcs(siteId);
  const registry = useVariationDraftStores({
    storeApi,
    seedsById,
    initialPreviewRecipientViewMode: canUseRcs ? "primary" : "fallback",
  });
  const addVariation = useAddSmsVariation(storeApi, registry);

  const variations = useBroadcastComposeStoreShallow((s) => s.variations);
  const activeVariationIndex = useBroadcastComposeStore(
    (s) => s.activeVariationIndex,
  );
  const sendMode = useBroadcastComposeStore((s) => s.sendMode);
  const scheduledAtIso = useBroadcastComposeStore((s) => s.scheduledAtIso);
  const planScheduleWindow = useBroadcastComposeStoreShallow(
    (s) => s.scheduleWindow,
  );
  // Plan drafts must carry a send time: without one the broadcast would come
  // back as an instant send, which the plan flow forbids.
  const missingPlanSendAt = Boolean(planScheduleWindow) && !scheduledAtIso;
  const abEnabled = variations.length > 1;
  const activeVariationId =
    variations[activeVariationIndex]?.id ?? variations[0].id;
  const activeCtx = registry.getContext(activeVariationId);

  const { data: dragonTiles } = useDragonTiles(siteId, {
    enabled: siteId.length > 0,
  });
  const innerTileId = useMemo(
    () =>
      dragonTiles
        ? getBroadcastInnerTileIdFromDragonTiles(dragonTiles)
        : undefined,
    [dragonTiles],
  );

  const [saveDraftBusy, setSaveDraftBusy] = useState(false);
  const [activateBusy, setActivateBusy] = useState(false);
  const abDurationInvalid = useIsAbDurationInvalid();

  const createFrameMutation = useMutation<
    Frame,
    Error,
    CreateFramePayloadParsed
  >({
    mutationKey: ["createBroadcastFrame", siteId],
    mutationFn: (payload) => createFrame(siteId, payload),
    onError: (error: unknown) => {
      enqueueErrorSnackbar(error, "Failed to create frame");
    },
  });

  const createBroadcastMutation = useMutation<
    { id: string },
    Error,
    CreateBroadcastRequestV2
  >({
    mutationKey: ["createBroadcast", siteId],
    mutationFn: (payload) => createBroadcastV2ViaAudiencesApi(siteId, payload),
    onError: (error: unknown) => {
      enqueueErrorSnackbar(error, "Failed to create broadcast");
    },
  });

  const activateBroadcastMutation = useMutation({
    mutationKey: ["activateBroadcastAfterCreate", siteId],
    mutationFn: (broadcastId: string) =>
      updateBroadcastStatusViaAudiencesApi(
        siteId,
        broadcastId,
        "BROADCAST_STATUS_ACTIVE",
      ),
    onError: (error: unknown) => {
      enqueueErrorSnackbar(error, "Failed to activate broadcast");
    },
  });

  const isPipelineBusy =
    createFrameMutation.isPending ||
    createBroadcastMutation.isPending ||
    activateBroadcastMutation.isPending;

  /** Create a frame per variation from its editor store; return frame-bearing drafts. */
  async function createVariationFrames(
    name: string,
  ): Promise<VariationDraft[]> {
    if (!innerTileId) {
      enqueueSnackbar(
        "Broadcast dragon tile is unavailable. Please try again later.",
        { variant: "error" },
      );
      throw new Error("Missing broadcast inner tile");
    }
    const current = storeApi.getState().variations;
    const created = await Promise.all(
      current.map((v, i) => {
        const state = registry.getStore(v.id).getState();
        const frameName = smsVariationFrameName(name, i, current.length);
        return createFrameMutation.mutateAsync(
          buildCreateBroadcastFramePayload({
            siteId,
            frameName,
            innerTileId,
            frameOutput: selectFrameOutput(state),
            productModel: selectSelectedProductModel(state),
          }),
        );
      }),
    );
    return current.map((v, i) => ({ ...v, frameId: created[i].id }));
  }

  // Every variation's authoring draft must pass all extension validators.
  function allVariationsValid() {
    return storeApi
      .getState()
      .variations.every((v) =>
        selectIsAuthoringValid(registry.getStore(v.id).getState()),
      );
  }

  async function handleActivate() {
    const state = storeApi.getState();
    // Throw (not return): the form shows a success animation on resolve.
    if (!allVariationsValid()) {
      enqueueSnackbar("Fix message errors before saving", { variant: "error" });
      throw new Error("Invalid authoring state");
    }
    setActivateBusy(true);
    try {
      const variationsWithFrames = await createVariationFrames(state.name);
      const payload = buildCreateSmsBroadcastRequestV2({
        name: state.name,
        audienceIds: state.audienceIds,
        excludedAudienceIds: state.excludedAudienceIds,
        sendMode: state.sendMode,
        scheduledAtIso: state.scheduledAtIso,
        primaryChannel: state.primaryChannel,
        variations: variationsWithFrames,
        abTest: state.abTest,
        smartSend: state.smartSend,
        reachOptimization: state.reachOptimization,
        reachOptimizationPercentage: state.reachOptimizationPercentage,
        enforceFrequencyCap: state.enforceFrequencyCap,
        ...calendarSourceStamp(returnTo),
      });
      const created = await createBroadcastMutation.mutateAsync(payload);
      // Instant sends are created already ACTIVE; scheduled sends stay DRAFT.
      if (state.sendMode !== "instant") {
        await activateBroadcastMutation.mutateAsync(created.id);
      }
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(siteId),
      });
    } finally {
      setActivateBusy(false);
    }
  }

  async function handleSaveDraft() {
    const state = storeApi.getState();
    if (!allVariationsValid()) {
      enqueueSnackbar("Fix message errors before saving", { variant: "error" });
      return;
    }
    const parsed = broadcastV2ComposeDraftFormSchema.safeParse({
      name: state.name,
      audienceIds: state.audienceIds,
      sendMode: state.sendMode,
      scheduledAtIso: state.scheduledAtIso,
      primaryChannel: state.primaryChannel,
    });
    if (!parsed.success) {
      enqueueSnackbar(parsed.error.issues[0]?.message ?? "Check the form", {
        variant: "error",
      });
      return;
    }

    setSaveDraftBusy(true);
    try {
      const variationsWithFrames = await createVariationFrames(state.name);
      const payload = buildDraftSmsBroadcastRequestV2({
        name: state.name,
        audienceIds: state.audienceIds,
        excludedAudienceIds: state.excludedAudienceIds,
        sendMode: state.sendMode,
        scheduledAtIso: state.scheduledAtIso,
        primaryChannel: state.primaryChannel,
        variations: variationsWithFrames,
        abTest: state.abTest,
        smartSend: state.smartSend,
        reachOptimization: state.reachOptimization,
        reachOptimizationPercentage: state.reachOptimizationPercentage,
        enforceFrequencyCap: state.enforceFrequencyCap,
        ...calendarSourceStamp(returnTo),
      });
      const created = await createBroadcastMutation.mutateAsync(payload);
      void queryClient.invalidateQueries({
        queryKey: broadcastV2Keys.all(siteId),
      });
      enqueueSnackbar("Saved as draft", { variant: "success" });
      if (returnTo === "marketing-calendar") {
        void navigate({
          to: "/marketing-calendar/$siteId/new",
          params: { siteId },
          search: { addedBroadcastId: created.id },
        });
      } else {
        void navigate({
          to: "/dragon-tiles/$siteId/broadcastV2/$broadcastId/sms",
          params: { siteId, broadcastId: created.id },
        });
      }
    } catch {
      // handled by mutation onError
    } finally {
      setSaveDraftBusy(false);
    }
  }

  const messageEditors = (
    <>
      {variations.map((v, i) => (
        <Box
          key={v.id}
          sx={{ display: i === activeVariationIndex ? "block" : "none" }}
        >
          <registry.MessageDraftStoreContext.Provider
            value={registry.getContext(v.id)}
          >
            {/* In A/B mode the editor sits inside the elevation-1 tab panel, so
                keep its shell for contrast; without A/B there's no panel, so
                drop the redundant shell. */}
            <SmsMessageFieldWrapper siteId={siteId} hideShell={!abEnabled} />
          </registry.MessageDraftStoreContext.Provider>
        </Box>
      ))}
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        minHeight: 0,
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Paper
        elevation={1}
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.tokens.outlines.defaultBordersLight}`,
        })}
      >
        <OrionPageHeader
          breadcrumbs={[
            {
              label: "Dragon Tiles",
              to: "/dragon-tiles/$siteId",
              params: { siteId },
            },
            { label: "Broadcast" },
          ]}
          title="Compose Tile"
          actions={
            <>
              <OrionBackButton
                to="/dragon-tiles/$siteId/broadcast/list"
                params={{ siteId }}
              />
              <Button
                variant="outlined"
                color="primary"
                disabled={
                  saveDraftBusy ||
                  activateBusy ||
                  sendMode === "instant" ||
                  abDurationInvalid ||
                  missingPlanSendAt
                }
                onClick={() => void handleSaveDraft()}
              >
                Save as Draft
              </Button>
            </>
          }
          paddingY={5}
          paddingX={5}
        />
      </Paper>
      <ComposerLayout>
        <AuthoringColumn widthPercent={50}>
          <registry.MessageDraftStoreContext.Provider value={activeCtx}>
            <BroadcastV2ComposeSmsForm
              timezone={timezone}
              sendWindowStart={sendWindowStart}
              sendWindowEnd={sendWindowEnd}
              onActivate={handleActivate}
              onAddVariation={addVariation}
              submitBusy={isPipelineBusy}
              renderMessageEditors={messageEditors}
            />
          </registry.MessageDraftStoreContext.Provider>
        </AuthoringColumn>
        <Box
          sx={(theme) => ({
            flex: "1 1 50%",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            [theme.breakpoints.down("md")]: {
              flex: "1 1 auto",
              width: "100%",
            },
          })}
        >
          {abEnabled && <RightColumnVariationTabs />}
          <Box
            sx={(theme) => ({
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background["paper-elevation-1"],
              borderTopLeftRadius: 0,
            })}
          >
            <registry.MessageDraftStoreContext.Provider value={activeCtx}>
              <SmsPreviewPanelWrapper
                siteId={siteId}
                innerTileId={innerTileId}
              />
            </registry.MessageDraftStoreContext.Provider>
          </Box>
        </Box>
      </ComposerLayout>
    </Box>
  );
}
